import React, { useState, useEffect } from 'react';
import { reportService, ReportFilters } from '../../services/reportService';
import { PDFGenerator } from '../../utils/pdfGenerator';
import { toast } from 'react-hot-toast';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';

interface Props {
    filters: ReportFilters;
    onPreview: (url: string, title: string) => void;
}

const AnalyticsReports: React.FC<Props> = ({ filters, onPreview }) => {
    const [activeTab, setActiveTab] = useState('byBrand');
    const [data, setData] = useState<any>({
        byBrand: [], byModel: [], bySize: [], byColor: [], byVendor: [], byPaymentMethod: []
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadData();
    }, [filters]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await reportService.getSalesAnalytics(filters);
            if (res.success) {
                setData(res.data);
            }
        } catch (error) {
            toast.error('Error al cargar datos analíticos');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        const period = {
            start: new Date(filters.startDate + 'T00:00:00'),
            end: new Date(filters.endDate + 'T23:59:59')
        };
        const gen = new PDFGenerator('p');
        
        let cols: string[] = [];
        let body: any[][] = [];
        let title = '';

        if (activeTab === 'byBrand') {
            title = 'Ventas por Marca';
            cols = ['Marca', 'Cantidad Vendida', 'Total Vendido', 'Ticket Promedio'];
            body = data.byBrand.map((r: any) => [r.marca, r.cantidad, `$${r.total.toFixed(2)}`, `$${r.ticketPromedio.toFixed(2)}`]);
        } else if (activeTab === 'byModel') {
            title = 'Ventas por Modelo';
            cols = ['Marca', 'Modelo', 'Cantidad Vendida', 'Total Vendido'];
            body = data.byModel.map((r: any) => [r.marca, r.modelo, r.cantidad, `$${r.total.toFixed(2)}`]);
        } else if (activeTab === 'bySize') {
            title = 'Ventas por Talla';
            cols = ['Talla', 'Cantidad Vendida', 'Total Vendido'];
            body = data.bySize.map((r: any) => [r.talla, r.cantidad, `$${r.total.toFixed(2)}`]);
        } else if (activeTab === 'byColor') {
            title = 'Ventas por Color';
            cols = ['Color', 'Cantidad Vendida', 'Total Vendido'];
            body = data.byColor.map((r: any) => [r.color, r.cantidad, `$${r.total.toFixed(2)}`]);
        } else if (activeTab === 'byVendor') {
            title = 'Ventas por Vendedora';
            cols = ['Vendedora', 'Cant. Ventas', 'Total Vendido', 'Ticket Promedio'];
            body = data.byVendor.map((r: any) => [r.vendedora, r.cantidad, `$${r.total.toFixed(2)}`, `$${r.ticketPromedio.toFixed(2)}`]);
        } else if (activeTab === 'byPaymentMethod') {
            title = 'Ventas por Método de Pago';
            cols = ['Método de Pago', 'Transacciones', 'Total Recibido'];
            body = data.byPaymentMethod.map((r: any) => [r.metodo, r.transacciones, `$${r.total.toFixed(2)}`]);
        }

        gen.generateTable(cols, body, title, period);
        onPreview(gen.getBlobUrl(), title);
    };

    const tabs = [
        { id: 'byBrand', name: 'Por Marca' },
        { id: 'byModel', name: 'Por Modelo' },
        { id: 'bySize', name: 'Por Talla' },
        { id: 'byColor', name: 'Por Color' },
        { id: 'byVendor', name: 'Por Vendedora' },
        { id: 'byPaymentMethod', name: 'Por Pago' }
    ];

    const currentData = data[activeTab] || [];

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
                    >
                        {tab.name}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                    <h3 className="font-semibold text-gray-800">Resultados Analíticos</h3>
                    <button 
                        onClick={handleExport} 
                        className="px-5 py-2.5 bg-indigo-600 text-white shadow-md hover:bg-indigo-700 hover:shadow-lg font-bold text-sm flex items-center gap-2 rounded-xl transition-all transform hover:-translate-y-0.5"
                    >
                        <DocumentArrowDownIcon className="w-5 h-5" />
                        Generar PDF Exportable
                    </button>
                </div>
                
                <div className="overflow-x-auto max-h-[500px]">
                    {loading ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <div className="animate-spin w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full mb-4"></div>
                            Calculando analíticas...
                        </div>
                    ) : currentData.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                            No hay datos suficientes para generar este análisis con los filtros actuales.
                        </div>
                    ) : (
                        <table className="w-full text-sm text-left whitespace-nowrap">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-100 sticky top-0 z-10 shadow-sm">
                                {activeTab === 'byBrand' && (
                                    <tr><th className="px-4 py-3">Marca</th><th className="px-4 py-3 text-right">Cantidad Vendida</th><th className="px-4 py-3 text-right">Total Vendido</th><th className="px-4 py-3 text-right">Ticket Promedio</th></tr>
                                )}
                                {activeTab === 'byModel' && (
                                    <tr><th className="px-4 py-3">Marca</th><th className="px-4 py-3">Modelo</th><th className="px-4 py-3 text-right">Cantidad Vendida</th><th className="px-4 py-3 text-right">Total Vendido</th></tr>
                                )}
                                {activeTab === 'bySize' && (
                                    <tr><th className="px-4 py-3">Talla</th><th className="px-4 py-3 text-right">Cantidad Vendida</th><th className="px-4 py-3 text-right">Total Vendido</th></tr>
                                )}
                                {activeTab === 'byColor' && (
                                    <tr><th className="px-4 py-3">Color</th><th className="px-4 py-3 text-right">Cantidad Vendida</th><th className="px-4 py-3 text-right">Total Vendido</th></tr>
                                )}
                                {activeTab === 'byVendor' && (
                                    <tr><th className="px-4 py-3">Vendedora</th><th className="px-4 py-3 text-right">Cant. Ventas</th><th className="px-4 py-3 text-right">Total Vendido</th><th className="px-4 py-3 text-right">Ticket Promedio</th></tr>
                                )}
                                {activeTab === 'byPaymentMethod' && (
                                    <tr><th className="px-4 py-3">Método de Pago</th><th className="px-4 py-3 text-right">Num. Transacciones</th><th className="px-4 py-3 text-right">Total Recibido</th></tr>
                                )}
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {currentData.map((row: any, i: number) => (
                                    <tr key={i} className="hover:bg-indigo-50/30 transition-colors">
                                        {activeTab === 'byBrand' && (
                                            <><td className="px-4 py-3 font-semibold text-indigo-900">{row.marca}</td><td className="px-4 py-3 text-right">{row.cantidad}</td><td className="px-4 py-3 text-right font-medium text-green-700">${row.total?.toLocaleString()}</td><td className="px-4 py-3 text-right text-gray-500">${row.ticketPromedio?.toLocaleString()}</td></>
                                        )}
                                        {activeTab === 'byModel' && (
                                            <><td className="px-4 py-3 text-gray-600">{row.marca}</td><td className="px-4 py-3 font-semibold text-indigo-900">{row.modelo}</td><td className="px-4 py-3 text-right">{row.cantidad}</td><td className="px-4 py-3 text-right font-medium text-green-700">${row.total?.toLocaleString()}</td></>
                                        )}
                                        {activeTab === 'bySize' && (
                                            <><td className="px-4 py-3 font-semibold">{row.talla}</td><td className="px-4 py-3 text-right">{row.cantidad}</td><td className="px-4 py-3 text-right font-medium text-green-700">${row.total?.toLocaleString()}</td></>
                                        )}
                                        {activeTab === 'byColor' && (
                                            <><td className="px-4 py-3 font-semibold">{row.color}</td><td className="px-4 py-3 text-right">{row.cantidad}</td><td className="px-4 py-3 text-right font-medium text-green-700">${row.total?.toLocaleString()}</td></>
                                        )}
                                        {activeTab === 'byVendor' && (
                                            <><td className="px-4 py-3 font-medium">{row.vendedora}</td><td className="px-4 py-3 text-right">{row.cantidad}</td><td className="px-4 py-3 text-right font-medium text-green-700">${row.total?.toLocaleString()}</td><td className="px-4 py-3 text-right text-gray-500">${row.ticketPromedio?.toLocaleString()}</td></>
                                        )}
                                        {activeTab === 'byPaymentMethod' && (
                                            <><td className="px-4 py-3 font-medium">{row.metodo}</td><td className="px-4 py-3 text-right">{row.transacciones}</td><td className="px-4 py-3 text-right font-medium text-green-700">${row.total?.toLocaleString()}</td></>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnalyticsReports;
